import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Nav, Row, Tab } from "react-bootstrap";
import { RouteEndpoints } from "../../services/route.service";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "../../redux/states";
import { StateKeys } from "../../redux/states/main.state";
import {
  AppState,
  CheckContentProviderIdExist,
  getContentProviderId,
  getLanguageByDefault,
  ResourceTypes,
} from "../../services/app.service";
import Main from "../../redux/actions/main.action";
import { RouteComponentProps, useParams } from "react-router";
import Loader from "../../components/Loader";
import {
  ClassContentDiscussionResponse,
  ClassResponse,
  ContentResponse,
} from "../../services/api.response.service";
import Api from "../../services/api.service";
import { Link } from "react-router-dom";
import Languages from "../../components/Languages";
import SubscribePayButton from "../../components/classCourse/SubscribePayButton";
import ClassOutline from "../../components/classCourse/ClassOutline";
import OverviewInfoCard from "../../components/classCourse/OverviewInfoCard";
import ClassesCardHList from "../../components/ClassesCardHList";
import CourseContentRead from "../../components/classCourse/CourseContentRead";
import CourseContentPractice from "../../components/classCourse/CourseContentPractice";
import CourseContentListen from "../../components/classCourse/CourseContentListen";
import CourseContentWatch from "../../components/classCourse/CourseContentWatch";
import CourseContentDiscuss, {
  DiscussFilter,
} from "../../components/classCourse/CourseContentDiscuss";
import CourseContentTest from "../../components/classCourse/CourseContentTest";
import ReadCheck from "../../assets/images/read_check.svg";

import { toInteger } from "lodash";
import { Mixpanel } from "../../services/mixpanel";
import {
  getCompletedListenCount,
  getTotalListenCount,
  getCompletedWatchCount,
  getTotalWatchCount,
  getCompletedTestCount,
  getTotalTestCount,
} from "../../utils/contentProgress";

function CourseContent({ location }: RouteComponentProps) {
  const stateAll = useSelector((state: IRootState) => state);
  const currentUser = stateAll.setting.user!;

  let { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const getLanguage = getLanguageByDefault();

  const [theClass, setTheClass] = useState<ClassResponse | null>(null);
  const [contentId, setContentId] = useState<string>();
  const [content, setContent] = useState<ContentResponse | null>(null);
  const [discussPosts, setDiscussPosts] = useState<
    ClassContentDiscussionResponse[] | []
  >([]);
  const [discussFilter, setDiscussFilter] = useState<DiscussFilter>(
    "current_topic",
  );
  // const [discussComments, setDiscussComments] = useState<ClassContentDiscussionResponse[]|[]>([]);
  // const [contentVideos, setContentVideos] = useState<ContentResponse[]|[]>([]);
  // const [contentAudios, setContentAudios] = useState<ContentResponse[]|[]>([]);
  // const [tests, setTests] = useState<ContentResponse[]|[]>([]);

  const { ecampus_id } = useSelector(
    (state: IRootState) => state.setting.user!,
  );
  const [mp, setMp] = useState<boolean>(false);
  const [activeContentTab, setActiveContentTab] = useState<string>("read");
  const [relatedClasses, setRelatedClasses] = useState<ClassResponse[]>([]);
  const [relatedClassesLoading, setRelatedClassesLoading] =
    useState<boolean>(false);

  useEffect(() => {
    if (!currentUser.ecampus_id) {
      window.location.href = RouteEndpoints.SIGN_IN;
      return;
    }
    setContentId(id);
    // getContent(id);
    // getDiscussPosts(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (contentId) {
      // after these just reload some parts of the UI and not all
      setMp(false);
      setContent(null);
      setDiscussPosts([]);
      setDiscussFilter("current_topic");

      getContent(contentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);

  const getContent = async (contentId: string | number) => {
    const contentObj = await Api.getClassContent(toInteger(contentId));
    if (contentObj) {
      setContent(contentObj.data.data);
      setTheClass(contentObj.data.data.content_class);
      return contentObj.data.data;
    }
    return null;
  };

  // console.log("acontent", content);

  const loadDiscussList = useCallback(
    async (filter: DiscussFilter) => {
      if (content?.id == null) return;
      if (filter === "all_classes" && content.class_id != null) {
        const res = await Api.getClassContentDiscussPosts(
          toInteger(content.class_id),
        );
        if (res?.data?.data) setDiscussPosts(res.data.data);
      } else {
        const res = await Api.getDiscussPosts(toInteger(content.id));
        if (res?.data?.data) setDiscussPosts(res.data.data);
      }
    },
    [content?.id, content?.class_id],
  );

  const handleDiscussFilterChange = useCallback(
    (next: DiscussFilter) => {
      setDiscussFilter(next);
      loadDiscussList(next);
    },
    [loadDiscussList],
  );

  useEffect(() => {
    if (content?.id == null) return;
    let cancelled = false;
    (async () => {
      const topicRes = await Api.getDiscussPosts(toInteger(content.id));
      if (cancelled) return;
      const topicList = topicRes?.data?.data ?? [];
      if (topicList.length > 0) {
        setDiscussPosts(topicList);
        setDiscussFilter("current_topic");
        return;
      }
      if (content.class_id != null) {
        const classRes = await Api.getClassContentDiscussPosts(
          toInteger(content.class_id),
        );
        if (cancelled) return;
        const classList = classRes?.data?.data ?? [];
        setDiscussPosts(classList);
        setDiscussFilter("all_classes");
      } else {
        setDiscussPosts([]);
        setDiscussFilter("current_topic");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [content?.id, content?.class_id]);

  const refreshDiscussPosts = useCallback(() => {
    loadDiscussList(discussFilter);
  }, [discussFilter, loadDiscussList]);

  const handleDefaultActiveKey = () =>
    content?.has_read
      ? "read"
      : content?.has_practice
        ? "practice"
        : content?.content_resources?.filter(
              (item) => item.type === ResourceTypes.VIDEO,
            ).length
          ? "watch"
          : content?.content_resources?.filter(
                (item) => item.type === ResourceTypes.AUDIO,
              ).length
            ? "listen"
            : content?.has_test
              ? "test"
              : "discuss";

  useEffect(() => {
    if (content) {
      setActiveContentTab(handleDefaultActiveKey());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.id]);

  const showClassInfoCard = theClass;

  const relatedCoursesViewAllLink = useMemo(() => {
    const cpId = theClass?.content_provider_id;
    if (cpId == null) return undefined;
    if (
      CheckContentProviderIdExist() &&
      String(cpId) === String(getContentProviderId() ?? "")
    ) {
      return RouteEndpoints.CONTENT_PROVIDER_CLASSES;
    }
    return RouteEndpoints.CONTENT_PROVIDER_DETAILS(String(cpId));
  }, [theClass?.content_provider_id]);

  useEffect(() => {
    if (!theClass?.content_provider_id) {
      setRelatedClasses([]);
      setRelatedClassesLoading(false);
      return;
    }
    setRelatedClassesLoading(true);
    Api.getContentProviderClassesFirstPage(theClass.content_provider_id)
      .then((res) => {
        const body = res?.data as { data?: ClassResponse[] };
        const raw = body?.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        const filtered = list
          .filter((c: ClassResponse) => c.id !== theClass.id)
          .slice(0, 12);
        setRelatedClasses(filtered);
      })
      .catch(() => setRelatedClasses([]))
      .finally(() => setRelatedClassesLoading(false));
  }, [theClass?.id, theClass?.content_provider_id]);

  const handleSetCourse = (id: string, title: string) => {
    dispatch(
      Main.togglePageState({
        stateKey: StateKeys.VIDEO,
        data: AppState.PAUSING,
      }),
    );
    dispatch(
      Main.togglePageState({
        stateKey: StateKeys.AUDIO,
        data: AppState.PAUSING,
      }),
    );
    dispatch(
      Main.postPageObjectDataSuccess({
        stateKey: StateKeys.VIDEO,
        data: {},
        clear: true,
      }),
    );
    dispatch(
      Main.postPageObjectDataSuccess({
        stateKey: StateKeys.AUDIO,
        data: {},
        clear: true,
      }),
    );
    setContentId(id);
    // window.location.href = RouteEndpoints.COURSE_CONTENT(id);
  };

  const handleContentTabChangeAction = (title: string) => {
    Mixpanel.track("Content Access - " + title, {
      with: ecampus_id ? "Auth" : "Guest",
      content_id: content?.id,
      content_name: content?.title,
      class_id: theClass?.id,
      class_name: theClass?.name,
      user_id: currentUser.id,
      ...(theClass?.content_provider_id
        ? { content_provider_id: theClass?.content_provider_id }
        : {}),
    });
    dispatch(
      Main.togglePageState({
        stateKey: StateKeys.VIDEO,
        data: AppState.PAUSING,
      }),
    );
    dispatch(
      Main.togglePageState({
        stateKey: StateKeys.AUDIO,
        data: AppState.PAUSING,
      }),
    );
  };

  useEffect(() => {
    if (
      !mp &&
      content &&
      theClass &&
      fullshow(theClass) &&
      theClass.is_subscribed &&
      theClass.has_paid
    ) {
      // Mixpanel.identify(user.id);
      Mixpanel.track("Content Access", {
        with: ecampus_id ? "Auth" : "Guest",
        content_id: content?.id,
        content_name: content?.title,
        class_id: theClass?.id,
        class_name: theClass?.name,
        user_id: currentUser.id,
        ...(theClass?.content_provider_id
          ? { content_provider_id: theClass?.content_provider_id }
          : {}),
      });
      setMp(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, theClass]);

  function fullshow(tclass: ClassResponse) {
    if (CheckContentProviderIdExist()) {
      return Number(getContentProviderId()) === tclass?.content_provider_id;
    }
    return true;
  }

  return (
    <div className="content-page scroller-hide">
      <Row>
        {theClass &&
          theClass?.is_subscribed &&
          theClass?.has_paid &&
          fullshow(theClass) && (
            // (content && content?.class_id) &&
            <>
              <Col sm={12}>
                <Row>
                  {
                    <Col
                      md={8}
                      className="ps-4 d-flex flex-column"
                      style={{ minHeight: 0 }}
                    >
                      {/*<GoBack />*/}
                      {!content && <Loader noCard imageLoader />}

                      {content && (
                        <>
                          <div className="course-content-panel">
                            <div className="course-content-scroll scrollbar-dd">
                              <Col sm={12} className="mb-3">
                                <h3
                                  style={{ color: "black" }}
                                  className="fw-bold"
                                >
                                  {content.title}
                                </h3>
                              </Col>
                              <Tab.Container defaultActiveKey={content.id}>
                                <Tab.Content>
                                  <Tab.Pane eventKey={content.id}>
                                    <Tab.Container
                                      activeKey={activeContentTab}
                                      onSelect={(k) =>
                                        k != null && setActiveContentTab(k)
                                      }
                                    >
                                      {/* Read | Listen | Watch | Practice | Discuss | Tests */}
                                      <div className="user-tabing">
                                        <Nav
                                          as="ul"
                                          variant="pills"
                                          className="d-flex align-items-center justify-content-start course-tab p-0 m-0 border-bottom"
                                        >
                                          {content.has_read && (
                                            <Nav.Item
                                              as="li"
                                              className=" col-12 col-sm-2 p-0 "
                                            >
                                              <Nav.Link
                                                eventKey="read"
                                                role="button"
                                                className="text-center p-3 d-flex align-items-center justify-content-center gap-1"
                                                onClick={() =>
                                                  handleContentTabChangeAction(
                                                    "Read",
                                                  )
                                                }
                                              >
                                                <Languages langKey="read" />
                                                {content.user_points?.some(
                                                  (point) =>
                                                    (
                                                      point as {
                                                        where?: string;
                                                      }
                                                    )?.where?.toLowerCase() ===
                                                    "read",
                                                ) && (
                                                  <span
                                                    style={{
                                                      marginLeft: "11px",
                                                    }}
                                                  >
                                                    <img
                                                      src={ReadCheck}
                                                      alt="Read complete"
                                                    />
                                                  </span>
                                                )}
                                              </Nav.Link>
                                            </Nav.Item>
                                          )}
                                          {!!content.content_resources.filter(
                                            (item) =>
                                              item.type === ResourceTypes.AUDIO,
                                          ).length && (
                                            <Nav.Item
                                              as="li"
                                              className="col-12 col-sm-2 p-0"
                                            >
                                              <Nav.Link
                                                eventKey="listen"
                                                role="button"
                                                className="text-center p-3 d-flex align-items-center justify-content-center gap-1"
                                                onClick={() =>
                                                  handleContentTabChangeAction(
                                                    "Listen",
                                                  )
                                                }
                                              >
                                                <Languages langKey="listen" />
                                                <span className="tab-progress-wrapper">
                                                  <span className="small tab-progress">
                                                    {getCompletedListenCount(
                                                      content,
                                                    )}
                                                    /
                                                    {getTotalListenCount(
                                                      content,
                                                    )}
                                                  </span>
                                                </span>
                                              </Nav.Link>
                                            </Nav.Item>
                                          )}

                                          {!!content.content_resources.filter(
                                            (item) =>
                                              item.type === ResourceTypes.VIDEO,
                                          ).length && (
                                            <Nav.Item
                                              as="li"
                                              className=" col-12 col-sm-2 p-0 "
                                            >
                                              <Nav.Link
                                                eventKey="watch"
                                                role="button"
                                                className="text-center p-3 d-flex align-items-center justify-content-center gap-1"
                                                onClick={() =>
                                                  handleContentTabChangeAction(
                                                    "Watch",
                                                  )
                                                }
                                              >
                                                <Languages langKey="watch" />
                                                <span className="tab-progress-wrapper">
                                                  <span className="small tab-progress">
                                                    {getCompletedWatchCount(
                                                      content,
                                                    )}
                                                    /
                                                    {getTotalWatchCount(
                                                      content,
                                                    )}
                                                  </span>
                                                </span>
                                              </Nav.Link>
                                            </Nav.Item>
                                          )}

                                          {content.has_practice && (
                                            <Nav.Item
                                              as="li"
                                              className="col-12 col-sm-2 p-0"
                                            >
                                              <Nav.Link
                                                eventKey="practice"
                                                role="button"
                                                className="text-center p-3 d-flex align-items-center justify-content-center gap-1"
                                                onClick={() =>
                                                  handleContentTabChangeAction(
                                                    "Practice",
                                                  )
                                                }
                                              >
                                                <Languages langKey="practice" />
                                                {content.user_points?.some(
                                                  (point) =>
                                                    (
                                                      point as {
                                                        where?: string;
                                                      }
                                                    )?.where?.toLowerCase() ===
                                                    "practice",
                                                ) && (
                                                  <span
                                                    style={{
                                                      marginLeft: "11px",
                                                    }}
                                                  >
                                                    <img
                                                      src={ReadCheck}
                                                      alt="Practice complete"
                                                    />
                                                  </span>
                                                )}
                                              </Nav.Link>
                                            </Nav.Item>
                                          )}

                                          <Nav.Item
                                            as="li"
                                            className="col-12 col-sm-2 p-0"
                                          >
                                            <Nav.Link
                                              eventKey="discuss"
                                              role="button"
                                              className="text-center p-3"
                                              onClick={() =>
                                                handleContentTabChangeAction(
                                                  "Discuss",
                                                )
                                              }
                                            >
                                              <Languages langKey="discuss" />
                                            </Nav.Link>
                                          </Nav.Item>

                                          {content.has_test && (
                                            <Nav.Item
                                              as="li"
                                              className="col-12 col-sm-2 p-0"
                                            >
                                              <Nav.Link
                                                eventKey="test"
                                                role="button"
                                                className="text-center p-3 d-flex align-items-center justify-content-center gap-1"
                                                onClick={() =>
                                                  handleContentTabChangeAction(
                                                    "Test",
                                                  )
                                                }
                                              >
                                                <Languages langKey="tests" />
                                                <span className="tab-progress-wrapper">
                                                  <span className="small tab-progress">
                                                    {getCompletedTestCount(
                                                      content,
                                                    )}
                                                    /
                                                    {getTotalTestCount(content)}
                                                  </span>
                                                </span>
                                              </Nav.Link>
                                            </Nav.Item>
                                          )}
                                        </Nav>
                                      </div>
                                      <Col sm={12} className="pt-3">
                                        <Tab.Content>
                                          {content.has_read && (
                                            <Tab.Pane
                                              eventKey="read"
                                              className="content-page"
                                            >
                                              <CourseContentRead
                                                content={content}
                                              />
                                            </Tab.Pane>
                                          )}
                                          {content.has_practice && (
                                            <Tab.Pane
                                              eventKey="practice"
                                              className="content-page"
                                            >
                                              <CourseContentPractice
                                                content={content}
                                              />
                                            </Tab.Pane>
                                          )}
                                          {!!content.content_resources.filter(
                                            (item) =>
                                              item.type === ResourceTypes.VIDEO,
                                          ).length && (
                                            <Tab.Pane
                                              eventKey="watch"
                                              className="content-page"
                                            >
                                              <CourseContentWatch
                                                content={content}
                                              />
                                            </Tab.Pane>
                                          )}
                                          {!!content.content_resources.filter(
                                            (item) =>
                                              item.type === ResourceTypes.AUDIO,
                                          ).length && (
                                            <Tab.Pane
                                              eventKey="listen"
                                              className="content-page"
                                            >
                                              <CourseContentListen
                                                content={content}
                                              />
                                            </Tab.Pane>
                                          )}
                                          {content.has_test && (
                                            <Tab.Pane
                                              eventKey="test"
                                              className="content-page"
                                            >
                                              <CourseContentTest
                                                content={content}
                                              />
                                            </Tab.Pane>
                                          )}
                                          <Tab.Pane
                                            eventKey="discuss"
                                            className="content-page"
                                          >
                                            <CourseContentDiscuss
                                              discussions={discussPosts}
                                              contentID={content?.id}
                                              onPostSuccess={refreshDiscussPosts}
                                              discussFilter={discussFilter}
                                              onDiscussFilterChange={
                                                handleDiscussFilterChange
                                              }
                                              showDiscussFilters={!!content}
                                            />
                                          </Tab.Pane>
                                        </Tab.Content>
                                      </Col>
                                    </Tab.Container>
                                  </Tab.Pane>
                                </Tab.Content>
                              </Tab.Container>
                            </div>
                          </div>
                        </>
                      )}

                      {showClassInfoCard && (
                        <>
                          <hr
                            style={{ marginBottom: "50px", marginTop: "40px" }}
                          />
                          <Col sm={12} className="mb-3">
                            <OverviewInfoCard data={theClass} />
                          </Col>
                          {(relatedClassesLoading ||
                            relatedClasses.length > 0) && (
                            <Col sm={12} className="mt-4">
                              <div
                                className={`related-courses-section ${
                                  relatedClassesLoading
                                    ? "related-courses-section--loading"
                                    : ""
                                }`}
                              >
                                <ClassesCardHList
                                  title="relatedCourses"
                                  viewAllLink={relatedCoursesViewAllLink}
                                  data={relatedClasses}
                                  state={
                                    relatedClassesLoading
                                      ? AppState.LOADING
                                      : AppState.LOADED
                                  }
                                />
                              </div>
                            </Col>
                          )}
                        </>
                      )}

                      {/*{details.pageState === AppState.ERROR_404 && (*/}
                      {/*  <NoData*/}
                      {/*    title="classNotFound"*/}
                      {/*    link={RouteEndpoints.HOME}*/}
                      {/*    btnText="backHome"*/}
                      {/*  />*/}
                      {/*)}*/}
                    </Col>
                  }
                  {theClass && (
                    <Col
                      md={4} // className="d-none d-md-block"
                    >
                      {/*<CourseOutline*/}
                      <ClassOutline
                        handleSetCourse={handleSetCourse}
                        contentId={toInteger(id)}
                        classId={theClass?.id}
                      />
                    </Col>
                  )}
                </Row>
              </Col>
            </>
          )}

        {
          //  if its whitelable and the content id not from the provider
          // theClass?.is_subscribed && theClass?.has_paid &&
          theClass && !fullshow(theClass) && (
            <div className="text-center pt-5 mt-5">
              <h5 className="pb-3">{getLanguage.sorryYouCannot}</h5>
              <Link
                to={{
                  // pathname: `https://web.ecampus.camp/content/${id}`,
                  pathname: `/home`,
                }}
                className="text-decoration-none btn btn-primary"
                target="_blank"
              >
                {getLanguage.backHome}
              </Link>
            </div>
          )
        }

        {
          //  if user has not subscribed to the class
          theClass && content && !theClass?.is_subscribed && (
            <div className="text-center pt-5 mt-5">
              <h5 className="pb-3">
                {/*<Languages langKey="subscribeToView" />*/}
              </h5>

              {currentUser?.ecampus_id && theClass?.is_subscribed ? (
                "" // 'subscribed'
              ) : (
                <>
                  <SubscribePayButton
                    classDetails={theClass}
                    courseDetails={content}
                  />
                  {/*<SubscribeAccessButton classDetails={theClass}/>*/}
                </>
              )}
              {/*<SubscribePayButton*/}
              {/*    classDetails={theClass}*/}
              {/*    courseDetails={content}*/}
              {/*/>*/}
            </div>
          )
        }

        {
          //  if user has subscribed but not paid
          theClass?.is_subscribed && !theClass?.has_paid && (
            <div className="text-center pt-5 mt-5">
              <h5 className="pb-3">{getLanguage.payToAccessContent}</h5>
              <a
                href={theClass.payment_url}
                target="_blank"
                // className="text-decoration-none btn btn-primary"
                className="btn btn-primary ec-btn"
                rel="noreferrer"
              >
                {getLanguage.payNow}
              </a>
            </div>
          )
        }

        {!content && !theClass && (
          <Col md={12}>
            {/*{details.pageState === AppState.LOADING &&*/}
            {/*  courses.pageState !== AppState.LOADED && */}
            <Loader noCard imageLoader />
          </Col>
        )}
      </Row>
    </div>
  );
}

export default CourseContent;
